// Pinyin normalization + equality utility.
// Accepts three input forms as equivalent:
//   - Diacritic:  "nǐ hǎo"
//   - Plain ASCII (no diacritics, no tones): "ni hao"
//   - Tone-number: "ni3 hao3"
// Additionally: case-insensitive, whitespace-collapsed, punctuation-stripped.

const DIACRITIC_MAP = {
  // a
  'ā': 'a1', 'á': 'a2', 'ǎ': 'a3', 'à': 'a4',
  // e
  'ē': 'e1', 'é': 'e2', 'ě': 'e3', 'è': 'e4',
  // i
  'ī': 'i1', 'í': 'i2', 'ǐ': 'i3', 'ì': 'i4',
  // o
  'ō': 'o1', 'ó': 'o2', 'ǒ': 'o3', 'ò': 'o4',
  // u
  'ū': 'u1', 'ú': 'u2', 'ǔ': 'u3', 'ù': 'u4',
  // ü
  'ǖ': 'v1', 'ǘ': 'v2', 'ǚ': 'v3', 'ǜ': 'v4', 'ü': 'v',
  'ń': 'n2', 'ň': 'n3', 'ǹ': 'n4',
  'ḿ': 'm2',
};

/**
 * Convert any pinyin input to a canonical toneless ASCII form.
 * Keeps only letters (no tones, no diacritics, no punctuation, no spaces).
 * "Nǐ hǎo, 世界!" → "nihao"
 * "ni3 hao3" → "nihao"
 * "NI HAO" → "nihao"
 */
export function normalizePinyin(input) {
  if (!input) return '';
  let s = String(input).toLowerCase();
  // Replace diacritic syllables with their tone-numbered form
  for (const [from, to] of Object.entries(DIACRITIC_MAP)) {
    s = s.split(from).join(to);
  }
  // Strip tone digits (1-5)
  s = s.replace(/[1-5]/g, '');
  // Replace ü/v mapping leftover
  s = s.replace(/v/g, 'u');
  // Strip anything non-letter (punctuation, CJK, whitespace)
  s = s.replace(/[^a-z]/g, '');
  return s;
}

/** True if the two pinyin strings match ignoring tones/diacritics/whitespace/case. */
export function pinyinEquivalent(a, b) {
  const na = normalizePinyin(a);
  const nb = normalizePinyin(b);
  return na.length > 0 && na === nb;
}
